import axios from 'axios';
import AuthService from './auth-header';
import invoiceService from './invoice-service';
import {
    listLocal,
    getLocalById,
    createLocal,
    updateLocal,
    updateStatusLocal,
    cloneLocal,
    getVersionsLocal,
    removeLocal,
    findBestForClient,
    isApiSuccess,
    shouldUseLocalFallback,
} from '@/utils/invoice-template-local-store';
import {
    applyInvoiceColumnTypeCatalog,
    observeTemplateColumnTypes,
} from '@/utils/invoice-column-types';
import { resolveInvoiceApiUrl } from '@/utils/invoice-api-url';

// /*dev*/const API_URL = 'https://ats.sattvahuman.in/api/';
// /*prod*/ //const API_URL = 'https://ats.sattvahuman.in/api/';

const API_URL = resolveInvoiceApiUrl()

/**
 * Some /invoice/config/* routes (sac-codes, credit-periods, payment-terms,
 * tds-percentages, gst-rates) are not implemented as standalone endpoints on
 * the backend. Return a normalized "unavailable" shape instead of the raw
 * axios error response so callers can rely on a consistent { status, data }
 * contract (mirrors the SERVICE_UNAVAILABLE pattern used in
 * reimbursement-service.js).
 */
/** Common GSTIN state codes used when upsert omits stateName/stateCode. */
const GSTIN_STATE_NAME_BY_CODE = {
    '01': 'Jammu and Kashmir', '02': 'Himachal Pradesh', '03': 'Punjab',
    '04': 'Chandigarh', '05': 'Uttarakhand', '06': 'Haryana', '07': 'Delhi',
    '08': 'Rajasthan', '09': 'Uttar Pradesh', '10': 'Bihar', '11': 'Sikkim',
    '12': 'Arunachal Pradesh', '13': 'Nagaland', '14': 'Manipur', '15': 'Mizoram',
    '16': 'Tripura', '17': 'Meghalaya', '18': 'Assam', '19': 'West Bengal',
    '20': 'Jharkhand', '21': 'Odisha', '22': 'Chhattisgarh', '23': 'Madhya Pradesh',
    '24': 'Gujarat', '27': 'Maharashtra', '29': 'Karnataka', '32': 'Kerala',
    '33': 'Tamil Nadu', '36': 'Telangana', '37': 'Andhra Pradesh',
}

/** Used when GET /invoice/config.bookingReasons is missing or []. */
const DEFAULT_BOOKING_REASONS = [
    { label: 'Revenue confirmed', value: 'Revenue confirmed' },
    { label: 'Client confirmation received', value: 'Client confirmation received' },
    { label: 'PO / WO received', value: 'PO / WO received' },
    { label: 'Candidate joined', value: 'Candidate joined' },
    { label: 'Service delivered', value: 'Service delivered' },
    { label: 'Monthly recurring billing', value: 'Monthly recurring billing' },
    { label: 'Adjustment / correction', value: 'Adjustment / correction' },
    { label: 'Other', value: 'Other' },
]

/** Used when GET /invoice/config.cancellationReasons is missing or []. */
const DEFAULT_CANCELLATION_REASONS = [
    { label: 'Early Attrition', value: 'Early Attrition' },
    { label: 'Invoice Filed and cannot be Amended', value: 'Invoice Filed and cannot be Amended' },
    { label: 'Bad Debt', value: 'Bad Debt' },
    { label: 'Client cancelled', value: 'Client cancelled' },
    { label: 'Duplicate invoice', value: 'Duplicate invoice' },
    { label: 'Other', value: 'Other' },
]

function normalizeGstin(value) {
    if (value == null) return ''
    return String(value).trim().toUpperCase().replace(/\s+/g, '')
}

function stateCodeFromGstin(gstin) {
    const m = /^(\d{2})/.exec(normalizeGstin(gstin))
    return m ? m[1] : ''
}

function panFromGstin(gstin) {
    const g = normalizeGstin(gstin)
    // GSTIN = state(2) + PAN(10) + entity(1) + Z + check(1)
    if (g.length >= 12) return g.slice(2, 12)
    return ''
}

/** In-flight + last-failed dedupe so Save + Next do not double-fire the same PUT. */
let _sattvaSaveInFlight = null
let _sattvaSaveInFlightKey = null
let _sattvaSaveLastFailedKey = null
let _sattvaSaveLastFailedAt = 0
let _sattvaSaveLastFailedResult = null

function sattvaSaveBodyKey(body) {
    try {
        return JSON.stringify(body)
    } catch (e) {
        return String(body && body.locationName) + ':' + String(body && body.locationId)
    }
}

function unavailable(label, data = []) {
    return {
        status: 'SERVICE_UNAVAILABLE',
        message: `${label} are not available yet. Please contact admin.`,
        data,
    };
}

function configUnavailable(err, label) {
    const code = err && err.response && err.response.status;
    if (code === 404 || code === 501 || !code) {
        return unavailable(label);
    }
    return err.response;
}

/** Returns the first defined/non-null value for `keys` found on `source`. */
function pickField(source, keys) {
    if (!source) return undefined;
    for (const key of keys) {
        const value = source[key];
        if (value !== undefined && value !== null) return value;
    }
    return undefined;
}

/**
 * Normalizes a getClientMasterData() row into canonical camelCase fields.
 *
 * ⚠️ FIELD-NAME UNCERTAINTY: the snake_case keys (`gstin_no`,
 * `company_address`, `state_name`, `supply_location`, ...) match what
 * CreateInvoice.vue has used in production, and are the best available
 * evidence of the real shape — but we've never inspected a live
 * authenticated response for THIS exact route
 * (`invoice/config/client-master-data`) directly (no test credentials
 * available in this environment; a user-reported "it returned real HPE
 * data" for this URL could not be re-verified byte-for-byte). If GST/
 * address/state/place-of-supply still show up blank in the UI for a client
 * you've confirmed via the network tab DOES have real master data, the
 * likely cause is a field-name mismatch here — open devtools → Network →
 * find the `client-master-data` request → check the actual response keys
 * → add the real key name to the matching `pickField(...)` list below
 * (camelCase and snake_case variants are both already covered defensively,
 * but an unanticipated name would still slip through as blank).
 */
export function normalizeClientMasterData(d) {
    if (!d) return null;
    return {
        clientName: pickField(d, ['client_name', 'clientName', 'name']) || '',
        legalName: pickField(d, ['legal_name', 'legalName']) || '',
        gstin: pickField(d, ['gstin_no', 'gstinNo', 'gstin', 'gst_no', 'gstNo', 'gst', 'clientGST']) || '',
        address: pickField(d, ['company_address', 'companyAddress', 'address', 'clientAddress']) || '',
        state: pickField(d, ['state_name', 'stateName', 'state', 'clientState']) || '',
        placeOfSupply: pickField(d, ['supply_location', 'supplyLocation', 'place_of_supply', 'placeOfSupply']) || '',
        poNumber: pickField(d, ['po_number', 'poNumber']) ?? null,
        creditPeriod: pickField(d, ['credit_period', 'creditPeriod']) ?? null,
        vertical: pickField(d, ['vertical']) || '',
        tdsRate: pickField(d, ['tds_rate', 'tdsRate', 'tds_percentage', 'tdsPercentage']) ?? null,
        einvoiceAddress: pickField(d, ['einvoice_address', 'einvoiceAddress']) || '',
        phone: pickField(d, ['client_phone', 'clientPhone', 'phone', 'contact_number', 'contactNumber', 'phone_no', 'phoneNo']) || '',
    };
}

/**
 * Normalizes a Sattva company/office-details row into canonical camelCase
 * fields, regardless of whether it came from the new dedicated
 * `getSattvaCompanyDetails()` endpoint (see that method's jsdoc for the
 * proposed contract) or the older `getMasterTableData()` fallback (whose
 * confirmed-working shape is `{ companyName, address, pan, bankName,
 * accountName, ifscCode, gst, lut (boolean), accountTypes, sacCodes }` —
 * see CreateInvoice.vue's loadMasterTableData()). The result is spread
 * *on top of* the raw row by callers (`{ ...raw, ...normalized }`), so any
 * extra fields the raw row already had (e.g. `accountTypes`, `sacCodes`)
 * keep working for existing call sites untouched, while new canonical
 * names (`accountNumber`, `lutNo`, `stateCode`, ...) become available for
 * data the old endpoint never exposed.
 */
export function normalizeSattvaCompanyDetails(d) {
    if (!d) return null;
    return {
        locationId: pickField(d, ['locationId', 'location_id']) ?? null,
        locationName: pickField(d, ['locationName', 'location_name', 'location', 'title']) || '',
        companyName: pickField(d, ['companyName', 'company_name']) || '',
        gstin: pickField(d, ['gstin', 'gst', 'gstNo', 'gst_no']) || '',
        pan: pickField(d, ['pan', 'panNo', 'pan_no']) || '',
        stateName: pickField(d, ['stateName', 'state_name', 'state']) || '',
        stateCode: pickField(d, ['stateCode', 'state_code']) || '',
        registeredAddress: pickField(d, ['registeredAddress', 'registered_address', 'address']) || '',
        // Older getMasterTableData() only ever exposed a boolean `lut` flag
        // (LUT applicable yes/no), never the actual LUT number — so this
        // will legitimately come back blank via the fallback path until the
        // new endpoint (or a backend change to the old one) supplies it.
        lutNo: pickField(d, ['lutNo', 'lut_no']) || (typeof d.lut === 'string' ? d.lut : ''),
        bankName: pickField(d, ['bankName', 'bank_name']) || '',
        accountName: pickField(d, ['accountName', 'account_name']) || '',
        // Not present on the old endpoint at all (previously unmappable
        // real data — see PUNCH_ATTENDANCE session notes); only populated
        // once the new dedicated endpoint ships.
        accountNumber: pickField(d, ['accountNumber', 'account_number', 'accountNo', 'account_no']) || '',
        ifscCode: pickField(d, ['ifscCode', 'ifsc_code', 'ifsc']) || '',
        accountType: pickField(d, ['accountType', 'account_type']) || '',
        logoUrl: pickField(d, ['logoUrl', 'logo_url']) || '',
    };
}

/** True when a Sattva company/bank row has at least one field we can show. */
function sattvaRowHasUsableFields(row) {
    if (!row || typeof row !== 'object' || Array.isArray(row)) return false;
    const n = normalizeSattvaCompanyDetails(row);
    return !!(n && (
        n.companyName || n.gstin || n.pan
        || n.registeredAddress || n.bankName || n.accountNumber
    ));
}

/**
 * Pull one office row out of aggregate `sattvaCompanyInfo`, which may be a
 * single object, an array, or a map keyed by locationId / location name.
 */
function pickSattvaCompanyInfoRow(info, locationId) {
    if (info == null) return null;

    const idStr = locationId != null && locationId !== '' ? String(locationId) : '';
    const rowMatchesLocation = (row) => {
        if (!row || typeof row !== 'object' || Array.isArray(row)) return false;
        if (!idStr) return false;
        const rid = row.locationId ?? row.location_id ?? row.id;
        return rid != null && String(rid) === idStr;
    };

    if (Array.isArray(info)) {
        if (idStr) {
            return info.find((row) => rowMatchesLocation(row) && sattvaRowHasUsableFields(row))
                || info.find(sattvaRowHasUsableFields)
                || null;
        }
        return info.find(sattvaRowHasUsableFields) || null;
    }

    if (typeof info !== 'object') return null;

    if (idStr) {
        const byKey = info[locationId] ?? info[idStr];
        if (sattvaRowHasUsableFields(byKey)) return byKey;
        const byId = Object.values(info).find((row) => rowMatchesLocation(row) && sattvaRowHasUsableFields(row));
        if (byId) return byId;
    }

    if (sattvaRowHasUsableFields(info)) return info;
    return null;
}

/**
 * True when a getClientMasterData() row looks incomplete/placeholder-ish —
 * no GSTIN and no address to auto-fill from.
 *
 * Why this exists: the backend's client-master-data table can have MULTIPLE
 * rows sharing the same `client_name` when a client has several legal
 * entities/business units/office locations (e.g. "HPE" has 5 rows with
 * different legal_name/gstin_no/company_address, one of which is entirely
 * blank). `GET invoice/config/client-master-data?clientName=...` returns
 * exactly ONE row chosen by the backend, and it isn't guaranteed to be a
 * row with real data — it can return the blank one.
 *
 * There is currently no known frontend-reachable endpoint/param to fetch ALL
 * rows for a given clientName (checked getMasterTableData()'s `locationId`
 * param — that's a different lookup, for Sattva's own issuing-office info,
 * not the client's legal entities), so callers can't offer a "pick the
 * right entity" selector. This is a best-effort client-side safety net:
 * flag the incomplete case so callers can warn the user instead of silently
 * leaving GST/address blank on the invoice.
 *
 * Uses normalizeClientMasterData()'s wide key-alias matching so this stays
 * accurate even if the real backend uses a key name we haven't seen yet
 * (reduces false "incomplete" positives caused by a field-name mismatch
 * rather than genuinely missing data).
 *
 * See normalizeClientMasterDataList() below for the multi-entity picker path
 * — once the backend returns all rows for a clientName, callers should let
 * the user pick the right entity instead of relying on this blank-row check.
 * This is kept as a safety net for whichever single entity ends up selected.
 */
export function isClientMasterDataIncomplete(d) {
    if (!d) return true;
    const n = normalizeClientMasterData(d);
    return !n.gstin && !n.address;
}

/**
 * Normalizes a getClientMasterData() response into a list of one entity per
 * legal entity/GSTIN/office location for the client (e.g. "Adani Power" has
 * separate rows per state with different `legal_name`, `gstin_no`,
 * `company_address`, `state_name`, `supply_location` — see
 * isClientMasterDataIncomplete()'s jsdoc for background).
 *
 * Expects the backend to return ALL matching rows as an array in `data` for
 * `GET invoice/config/client-master-data?clientName=...`. Still handles the
 * old single-object shape (or exactly one match) by treating it as a
 * one-entity list, so callers work whether or not the backend change has
 * shipped yet.
 */
export function normalizeClientMasterDataList(data) {
    const rows = Array.isArray(data) ? data : (data ? [data] : []);
    return rows.map((row, idx) => {
        const n = normalizeClientMasterData(row);
        return {
            id: (row && (row.id ?? row.ID ?? row.Id)) ?? idx,
            ...n,
            raw: row,
        };
    });
}

/** Unique state names from normalized client-master-data entities (sorted). */
export function extractClientStates(entities) {
    const seen = new Map();
    (entities || []).forEach((e) => {
        const s = (e && e.state ? String(e.state) : '').trim();
        if (s) {
            const key = s.toLowerCase();
            if (!seen.has(key)) seen.set(key, s);
        }
    });
    return Array.from(seen.values()).sort((a, b) => a.localeCompare(b));
}

/**
 * Pick the best entity row for a selected state. Prefers rows with both
 * GSTIN and address filled, then GSTIN-only, then first match.
 */
export function findClientEntityByState(entities, stateName) {
    if (!stateName || !entities || !entities.length) return null;
    const norm = String(stateName).trim().toLowerCase();
    const matches = entities.filter(
        (e) => (e.state || '').trim().toLowerCase() === norm,
    );
    return (
        matches.find((e) => e.gstin && e.address)
        || matches.find((e) => e.gstin)
        || matches[0]
        || null
    );
}

/**
 * Unique legal-entity names from normalized client-master-data entities
 * (sorted) — this is the disambiguator the State selector alone can't
 * provide: `getClientMasterData(clientName)` already returns every
 * legal-entity row for that client name in one call (see
 * normalizeClientMasterDataList()), and it's entirely possible for two
 * different legal entities of the same client to be registered in the SAME
 * state with different GSTINs (e.g. two group-company subsidiaries with
 * offices in the same city) — a case the state-only picker silently
 * resolves by guessing ("prefers the row with GSTIN+address filled") rather
 * than letting the user choose. Callers should surface this alongside (not
 * instead of) the state picker so the user can pick the exact legal entity
 * when a client name maps to more than one.
 */
export function extractClientLegalNames(entities) {
    const seen = new Map();
    (entities || []).forEach((e) => {
        const n = (e && e.legalName ? String(e.legalName) : '').trim();
        if (n) {
            const key = n.toLowerCase();
            if (!seen.has(key)) seen.set(key, n);
        }
    });
    return Array.from(seen.values()).sort((a, b) => a.localeCompare(b));
}

/**
 * Pick the best entity row for a selected legal name. Prefers rows with
 * both GSTIN and address filled, then GSTIN-only, then first match — same
 * preference order as findClientEntityByState(), used as the tie-breaker in
 * the (expected to be rare) case where a legal name still maps to more than
 * one row (e.g. duplicate/legacy master-data rows for the same entity).
 */
export function findClientEntityByLegalName(entities, legalName) {
    if (!legalName || !entities || !entities.length) return null;
    const norm = String(legalName).trim().toLowerCase();
    const matches = entities.filter(
        (e) => (e.legalName || '').trim().toLowerCase() === norm,
    );
    return (
        matches.find((e) => e.gstin && e.address)
        || matches.find((e) => e.gstin)
        || matches[0]
        || null
    );
}

/**
 * Entities whose legal_name matches (case-insensitive). Used to filter the
 * State dropdown to states belonging to the selected legal entity. When
 * `legalName` is blank, returns the full list unchanged.
 */
export function filterClientEntitiesByLegalName(entities, legalName) {
    if (!entities || !entities.length) return [];
    if (!legalName) return entities.slice();
    const norm = String(legalName).trim().toLowerCase();
    return entities.filter(
        (e) => (e.legalName || '').trim().toLowerCase() === norm,
    );
}

/**
 * Best entity for a legal name + state pair. Prefers GSTIN+address filled,
 * then GSTIN-only, then first match — same preference as the single-key
 * finders. Falls back to state-only match when no legal-name match exists
 * (e.g. client-name fallback where entities have blank legal_name).
 */
export function findClientEntityByLegalNameAndState(entities, legalName, stateName) {
    if (!stateName || !entities || !entities.length) return null;
    const byLegal = filterClientEntitiesByLegalName(entities, legalName);
    const pool = byLegal.length ? byLegal : entities;
    return findClientEntityByState(pool, stateName);
}

/**
 * Vertical → invoice-number prefix mapping.
 *
 * "Service Industry" invoices use "SH" (e.g. SH180), "Infra Industry" uses
 * "SHI" (e.g. SHI435) — confirmed from real invoice PDFs analyzed this
 * session (Hinduja/HG Infra/GPS docs all use SHI for infra, existing
 * `.includes('SH')` validation matches both since "SHI".includes("SH")).
 *
 * There are currently only these two verticals in the app (see
 * `verticalOptions` in InvoiceCreateManual.vue / CreateInvoice.vue /
 * WizardMoreDetailsStep.vue) — if a new vertical is ever added without
 * updating this map, getInvoicePrefixForVertical() returns null and callers
 * should fall back to leaving the Invoice # field manually editable rather
 * than guessing a prefix.
 */
export const INVOICE_PREFIX_BY_VERTICAL = {
    'Service Industry': 'SH',
    'Infra Industry': 'SHI',
};

/** Returns the invoice-number prefix for a vertical, or null if unmapped. */
export function getInvoicePrefixForVertical(vertical) {
    if (!vertical) return null;
    return INVOICE_PREFIX_BY_VERTICAL[vertical] || null;
}

/**
 * Next sequential invoice number for a prefix (e.g. "SH" → "SH180").
 *
 * PROPOSED CONTRACT (backend team — not implemented yet):
 *   GET /invoice/config/next-invoice-number?prefix=SHI
 *   Response 200: { "status": "OK", "data": { "nextNumber": "SHI436" } }
 *   MUST be backed by a persisted, atomically-incremented counter per
 *   prefix (e.g. a `invoice_number_counters` table with columns
 *   `prefix`, `last_number`, updated inside the same DB transaction that
 *   creates the invoice, using a row lock / `SELECT ... FOR UPDATE` or
 *   equivalent) — NOT computed by scanning existing invoices at request
 *   time, which would still race under concurrent requests.
 *
 * FALLBACK (until the backend ships the above): computes
 * max(existing invoice numbers matching this prefix) + 1 by scanning
 * `GET invoice` (see _nextInvoiceNumberFallback()). This has a genuine
 * RACE-CONDITION RISK — two users creating invoices for the same prefix at
 * nearly the same time can both compute the same "next" number, since
 * nothing reserves it until each invoice is actually submitted. It is a
 * best-effort UX improvement only, not a substitute for a backend atomic
 * counter, and callers should treat `_source: 'frontend-computed-fallback'`
 * as a signal to keep the field visibly editable/regenerable so a user can
 * recover if a collision is rejected by the backend on submit.
 *
 * Live Sattva backend currently 500s this route (missing handler / missing
 * `invoice_number_counters` table — see INVOICE_BACKEND_CHANGES_REQUIRED.md).
 * Probing it on every Vertical change / Regenerate is the HTTP 500 Invoice
 * Tracker create shows in DevTools. Do not call it until the atomic counter
 * ships; set PROBE_NEXT_INVOICE_NUMBER_API true then. A session-level
 * unavailable flag still swallows repeat 500s if a probe is turned on and
 * the route is still down.
 */
const PROBE_NEXT_INVOICE_NUMBER_API = false
let _nextInvoiceNumberApiUnavailable = !PROBE_NEXT_INVOICE_NUMBER_API

export async function getNextInvoiceNumber(prefix) {
    if (!prefix) {
        return { status: 'ERROR', message: 'No prefix supplied' };
    }
    if (!_nextInvoiceNumberApiUnavailable) {
        try {
            const res = await axios.get(API_URL + 'invoice/config/next-invoice-number', {
                params: { prefix },
                headers: AuthService.authHeader(),
            });
            const data = res.data;
            if (data && data.status === 'OK' && data.data && data.data.nextNumber) {
                return { status: 'OK', data: { nextNumber: data.data.nextNumber }, _source: 'backend-counter' };
            }
            _nextInvoiceNumberApiUnavailable = true;
        } catch (err) {
            if (shouldUseLocalFallback(err, err.response && err.response.data)) {
                _nextInvoiceNumberApiUnavailable = true;
            } else {
                return err.response;
            }
        }
    }
    return _nextInvoiceNumberFallback(prefix);
}

/**
 * Fetches the FULL invoice history (across `GET invoice`'s date/status/
 * client/category filters left empty, i.e. "everything") for use by the
 * next-invoice-number fallback below, defensively handling pagination.
 *
 * We have no confirmed evidence either way on whether this endpoint
 * paginates server-side — every existing call site in the app (Invoice-
 * Dashboard.vue, InvoiceTracker.vue) just reads `res.data.data` as "the
 * whole result set" for a date range and never checks for a second page.
 * To be safe for a MAX-across-all-invoices computation (silently scanning
 * only page 1 of a paginated response would compute a systematically
 * wrong/too-low next number), this walks pages using the optional `page`/
 * `limit` params added to `invoiceService.getAll()`, and adapts to
 * whatever the backend actually does:
 *   - No pagination (returns everything every time): first page already
 *     has every row, so `list.length < PAGE_SIZE` is true and we stop
 *     after ONE call — no extra overhead in the common case.
 *   - True pagination via `page`/`limit`: keeps requesting subsequent
 *     pages (using any `totalPages`/`pagination.totalPages` metadata if
 *     present, else "stop when a page returns fewer than `limit` rows")
 *     until the full set is collected.
 *   - Backend ignores `page`/`limit` entirely and just always returns the
 *     same (large) full list regardless of the `page` param: detected via
 *     "this page added zero previously-unseen rows" and stopped after a
 *     second, redundant call rather than looping forever.
 *   - A hard `MAX_PAGES` cap is kept as a last-resort safety net against
 *     any other unanticipated backend behavior causing an infinite loop.
 */
async function _fetchAllInvoicesForNumbering() {
    // Wide, explicit date range rather than relying on empty-string sd/ed
    // being treated as "no filter" server-side (observed elsewhere in the
    // app, e.g. InvoiceDashboard.vue, but not confirmed as a documented
    // contract) — this covers the entire invoice history either way.
    const sd = '2000-01-01';
    const ed = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
    const PAGE_SIZE = 1000;
    const MAX_PAGES = 50; // safety net: up to 50,000 invoices scanned

    const all = [];
    const seenKeys = new Set();
    let page = 1;

    while (page <= MAX_PAGES) {
        // eslint-disable-next-line no-await-in-loop
        const res = await invoiceService.getAll(sd, ed, '', '', '', 'invoice_date', page, PAGE_SIZE);
        if (!res || res.status !== 'OK') break;
        const list = Array.isArray(res.data && res.data.data)
            ? res.data.data
            : (Array.isArray(res.data) ? res.data : []);
        if (!list.length) break;

        let newRows = 0;
        list.forEach((inv) => {
            const key = (inv && (inv.id ?? inv.invoice_id ?? inv.invoice_number)) ?? JSON.stringify(inv);
            const dedupeKey = String(key);
            if (!seenKeys.has(dedupeKey)) {
                seenKeys.add(dedupeKey);
                all.push(inv);
                newRows += 1;
            }
        });

        const meta = res.data || {};
        const totalPages = meta.totalPages
            || (meta.pagination && meta.pagination.totalPages)
            || null;
        if (totalPages) {
            if (page >= totalPages) break;
        } else if (list.length < PAGE_SIZE) {
            // Fewer rows than the requested page size ⇒ this was the last
            // page (or the backend doesn't paginate at all and this was
            // simply the whole result set).
            break;
        } else if (newRows === 0) {
            // `page`/`limit` were silently ignored and we got the same
            // rows again — no real pagination support; stop rather than
            // re-fetching identical data indefinitely.
            break;
        }
        page += 1;
    }
    return all;
}

/**
 * Frontend-only fallback for getNextInvoiceNumber() — see its jsdoc above
 * for the race-condition caveat. Scans the FULL `GET invoice` history (via
 * `_fetchAllInvoicesForNumbering()` above, which handles pagination) for
 * IDs matching `^{prefix}(\d+)$` — anchored so e.g. prefix "SH" can never
 * accidentally match "SHI435" (no digit immediately follows "SH" there),
 * and legacy/malformed IDs that don't fit the pattern are simply ignored
 * rather than corrupting the max — and returns max+1. Defaults to
 * `{prefix}1` if the history is empty or unreadable (first invoice ever
 * for that prefix, or the list endpoint itself failed).
 */
async function _nextInvoiceNumberFallback(prefix) {
    const escaped = prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp(`^${escaped}(\\d+)$`, 'i');
    try {
        const list = await _fetchAllInvoicesForNumbering();
        let max = 0;
        list.forEach((inv) => {
            const id = String((inv && (inv.invoice_id || inv.invoice_number)) || '').trim();
            const m = id.match(re);
            if (m) {
                const n = parseInt(m[1], 10);
                if (!Number.isNaN(n) && n > max) max = n;
            }
        });
        return {
            status: 'OK',
            data: { nextNumber: `${prefix}${max + 1}` },
            _source: 'frontend-computed-fallback',
            _scannedCount: list.length,
            _warning: 'Computed client-side from existing invoice history — race condition possible with concurrent invoice creation. Needs a real backend atomic counter for production correctness.',
        };
    } catch (err) {
        return {
            status: 'OK',
            data: { nextNumber: `${prefix}1` },
            _source: 'frontend-computed-fallback-default',
            _warning: 'Could not read invoice history — defaulted to the first number for this prefix. Please double-check before submitting.',
        };
    }
}

/** Empty invoiceClientInfo object for resets between client/state changes. */
export function emptyInvoiceClientInfo() {
    return {
        clientName: '',
        legalName: '',
        clientGST: '',
        clientAddress: '',
        clientState: '',
        placeOfSupply: '',
        clientPhone: '',
    };
}

/**
 * How long a resolved getMasterTableData() response is reused by
 * getSattvaCompanyInfo() and friends. These are typically all invoked
 * together on page load (see CreateInvoice.vue / InvoiceCreateManual.vue),
 * so sharing one request avoids firing several near-simultaneous calls for
 * the same data.
 */
const MASTER_DATA_CACHE_TTL_MS = 15000;

/**
 * Same idea as MASTER_DATA_CACHE_TTL_MS, but for _getSharedInvoiceConfig()
 * below (the confirmed-working `GET invoice/config` aggregate endpoint).
 */
const INVOICE_CONFIG_CACHE_TTL_MS = 15000;

class InvoiceConfigService {
    constructor() {
        this._usingSessionTemplates = false;
        this._masterDataCache = new Map();
        this._invoiceConfigCache = null;
        this._sessionFallbackWarned = false;
        // Dedicated /invoice/config/sattva-company-details and bank-details
        // sub-routes are not shipped on some backends (404). Remember that
        // so location selection does not keep hitting missing URLs.
        this._sattvaCompanyDetailsMissing = false;
        this._bankDetailsMissing = false;
        this._clientMasterByLocationMissing = false;
        this._sattvaCompanyInfoMissing = false;
        this._sattvaCompanyInfoCache = null;
    }

    /**
     * True when template APIs fell back to session in-memory store
     * (NOT localStorage — browser persistence for templates is disabled).
     */
    get usingSessionFallback() {
        return this._usingSessionTemplates;
    }

    /** @deprecated Use usingSessionFallback — localStorage is no longer used. */
    get usingLocalStorage() {
        return this._usingSessionTemplates;
    }

    _templatePath(suffix = '') {
        return API_URL + 'invoice/config/templates' + suffix;
    }

    /**
     * Try the real template API first. On 404/501/network only, use the
     * session in-memory store (lost on refresh). Never writes localStorage.
     * Mutating operations tag `_source: 'session'` and `_persistWarning` so
     * the UI can toast that the DB is required.
     */
    async _withTemplateFallback(apiFn, localFn, errorResult = null, { mutating = false } = {}) {
        try {
            const res = await apiFn();
            if (isApiSuccess(res.data)) {
                this._usingSessionTemplates = false;
                return res.data;
            }
            if (res.data && res.data.status === 'NOT_IMPLEMENTED') {
                throw Object.assign(new Error('NOT_IMPLEMENTED'), { response: res });
            }
            // Non-fallback API error (validation etc.) — surface as-is
            if (res.data) return res.data;
        } catch (err) {
            if (!shouldUseLocalFallback(err, err.response && err.response.data)) {
                return errorResult || (err.response && err.response.data) || err.response;
            }
        }
        this._usingSessionTemplates = true;
        const result = localFn();
        if (result && typeof result === 'object') {
            result._source = result._source || 'session';
            if (mutating) {
                result._persistWarning =
                    'Template API unavailable — saved in this browser session only. Backend persistence is required; data will be lost on refresh.';
            }
        }
        return result;
    }
    // Master table & company info
    //
    // Location-specific Sattva issuing-office row. This is the path that
    // actually auto-filled company/bank fields before the July 2026 rewrite:
    // CreateInvoice.loadMasterTableData() → GET invoice/client-master-data?locationId=<numeric>.
    // Confirmed shape: { companyName, address, pan, bankName, accountName,
    // ifscCode, gst, lut, accountTypes, sacCodes }. The 400s were from sending
    // `locationId=Pune` (city name); callers now send the numeric /locations id.
    // Used again by _sattvaCompanyDetailsFromExistingConfig(). Do not call
    // `sattva-company-info?locationId=` — that query 404s; the unscoped
    // company-info list is fetched separately if this row is empty.
    async getMasterTableData(locationId) {
        try {
            const params = locationId ? { locationId } : {};
            const res = await axios.get(API_URL + 'invoice/client-master-data', {
                params,
                headers: AuthService.authHeader()
            });
            return res.data;
        } catch (err) {
            const code = err && err.response && err.response.status;
            if (code === 404 || code === 501) {
                this._clientMasterByLocationMissing = true;
                return unavailable('Sattva company/master data for this location');
            }
            if (code === 400) {
                // Backend rejected this locationId value. Callers now resolve the
                // location record's numeric `id` (from the same /locations list that
                // populates the dropdown) before calling this — see
                // CreateInvoice.vue/InvoiceCreateManual.vue. If it still 400s (e.g. no
                // matching id was found, or the backend actually expects something
                // else entirely), degrade gracefully instead of leaving a raw HTTP
                // error response in the caller's hands.
                // ⚠️ NEEDS BACKEND CONFIRMATION: exact expected format of `locationId`.
                return unavailable('Sattva company/master data for this location');
            }
            return err.response;
        }
    }

    /**
     * Shared, short-lived wrapper around getMasterTableData(). Concurrent
     * callers (e.g. getSattvaCompanyInfo()-adjacent lookups fired in a
     * Promise.all on page load) share one in-flight request; a resolved
     * response is then reused for MASTER_DATA_CACHE_TTL_MS so a burst of
     * calls only hits the network once. Cached per locationId ('' when none
     * is supplied).
     */
    async _getSharedMasterData(locationId = null) {
        const key = locationId || '';
        const cached = this._masterDataCache.get(key);
        if (cached) {
            if (cached.promise) return cached.promise;
            if (Date.now() - cached.timestamp < MASTER_DATA_CACHE_TTL_MS) return cached.value;
        }
        const promise = this.getMasterTableData(locationId)
            .then((result) => {
                this._masterDataCache.set(key, { value: result, timestamp: Date.now() });
                return result;
            })
            .catch((err) => {
                this._masterDataCache.delete(key);
                throw err;
            });
        this._masterDataCache.set(key, { promise });
        return promise;
    }

    /**
     * Sattva Company Information — dedicated endpoint (PROPOSED, not yet
     * implemented on the backend as of 2026-07-16).
     *
     * WHY: "Sattva Company Information" (company name, GSTIN, PAN,
     * registered address, bank name, account name, account number, IFSC,
     * LUT number) is currently piggy-backed on getMasterTableData() /
     * `GET invoice/client-master-data?locationId=...` — an endpoint whose
     * name/shape is really about the CLIENT master table, not a proper
     * "our own company registration details per issuing office" resource.
     * It also has no `account_number` or `lut_no` (string) fields at all,
     * even though both are real data that invoices need (see the GPS
     * SHI435 invoice walkthrough this session — bank account number and
     * LUT number literally have nowhere to go today).
     *
     * PROPOSED CONTRACT (for the backend team — this is not implemented
     * server-side yet, see the fallback behavior below):
     *   GET /invoice/config/sattva-company-details
     *   Query params:
     *     - locationId (integer, optional) — the same numeric id used by
     *       the existing `/locations` list (and by getMasterTableData()).
     *       When provided, response `data` is a SINGLE object for that
     *       location. When omitted, response `data` is an ARRAY of every
     *       configured location's details (lets the frontend prefetch all
     *       of them once instead of one request per location, if that
     *       ever becomes useful for e.g. a location picker preview).
     *   Response 200 (single-object mode):
     *     {
     *       "status": "OK",
     *       "message": "Sattva company details loaded",
     *       "data": {
     *         "locationId": 1,                              // int, required
     *         "locationName": "Pune",                       // string, required — should match /locations' `title`
     *         "companyName": "Sattva Human Private Limited", // string, required
     *         "gstin": "27ABDCS0586N1ZT",                   // string, required
     *         "pan": "ABDCS0586N",                           // string, required
     *         "stateName": "Maharashtra",                    // string, required
     *         "stateCode": "27",                             // string, required (2-digit GSTIN state code)
     *         "registeredAddress": "A-401, Marvel Bounty, ...", // string, required
     *         "lutNo": "AD270326119388U",                    // string, optional (null if not export/LUT-based)
     *         "bankName": "HDFC Bank",                        // string, required
     *         "accountName": "Sattva Human Private Limited",  // string, required
     *         "accountNumber": "50200046864261",              // string, required — NEW, not in old table
     *         "ifscCode": "HDFC0000486",                       // string, required
     *         "accountType": "Current Account",                // string, optional
     *         "logoUrl": "https://.../logo.png",               // string, optional
     *         "isActive": true                                 // boolean, optional (soft-disable a closed office)
     *       }
     *     }
     *   Response 200 (list-all mode, no locationId): `"data": [ { ...same shape... }, ... ]`
     *   Response 404: no row for the given locationId (frontend degrades gracefully — see below).
     *
     * Lookup order (restored 2026-08-26 — location select used to work):
     *   1. GET invoice/client-master-data?locationId=  (April 2026 working path)
     *   2. GET invoice/config → data.sattvaCompanyInfo / bankDetails
     *   3. GET invoice/config/sattva-company-info  (no query — ?locationId= 404s
     *      on this backend; pick the office client-side)
     * Dedicated GET sattva-company-details is skipped (same 404-with-id
     * pattern); PUT save still uses it.
     */
    async getSattvaCompanyDetails(locationId) {
        return this._sattvaCompanyDetailsFromExistingConfig(locationId);
    }

    /**
     * Upsert Sattva issuing-office company/bank details into
     * `invoice_sattva_company_details` (PROPOSED — backend may still 404).
     *
     * PROPOSED CONTRACT:
     *   PUT /invoice/config/sattva-company-details
     *   (POST accepted as alias with the same body)
     *   Body: {
     *     location: string,          // required by backend validation — same as locationName
     *     locationName: string,      // e.g. "Pune" (also sent as location_name)
     *     locationId: number|null,   // numeric /locations id when known (omit if unknown)
     *     companyName, gstin, pan, registeredAddress,
     *     stateName?, stateCode?, lutNo?,
     *     bankName, accountName, accountNumber, ifscCode, accountType?,
     *     logoUrl?, isActive?
     *   }
     *   Response 200/201: { status: 'OK', data: { …same shape as GET… } }
     *
     * Soft-fails with SERVICE_UNAVAILABLE on 404/501/400/422 so Create Invoice can
     * continue with invoice-only `invoiceSattvaInfo` without blocking. Identical
     * in-flight / recently-failed bodies are deduped (no double PUT).
     */
    async saveSattvaCompanyDetails(payload = {}) {
        const gstin = normalizeGstin(payload.gstin || payload.gst || payload.gstNo || '');
        const derivedStateCode = stateCodeFromGstin(gstin);
        const derivedPan = panFromGstin(gstin);
        let locationId = payload.locationId ?? payload.location_id ?? null;
        if (locationId != null && locationId !== '') {
            const asNum = Number(locationId);
            // Never send city-name strings as locationId (historical 400: locationId=Pune).
            if (Number.isFinite(asNum) && String(asNum) === String(locationId).trim()) {
                locationId = asNum;
            } else {
                locationId = null;
            }
        } else {
            locationId = null;
        }
        const raw = {
            locationId,
            locationName: String(
                payload.locationName || payload.location_name || payload.location || '',
            ).trim(),
            companyName: payload.companyName || payload.company_name || '',
            gstin,
            pan: String(payload.pan || payload.panNo || derivedPan || '').trim().toUpperCase(),
            stateName: payload.stateName || payload.state_name || payload.state
                || (derivedStateCode && GSTIN_STATE_NAME_BY_CODE[derivedStateCode])
                || '',
            stateCode: payload.stateCode || payload.state_code || derivedStateCode || '',
            registeredAddress: payload.registeredAddress || payload.registered_address || payload.address || '',
            lutNo: payload.lutNo || payload.lut_no || '',
            bankName: payload.bankName || payload.bank_name || '',
            accountName: payload.accountName || payload.account_name || '',
            accountNumber: payload.accountNumber || payload.account_number || payload.accountNo || '',
            ifscCode: String(payload.ifscCode || payload.ifsc_code || payload.ifsc || '').trim().toUpperCase(),
            accountType: payload.accountType || payload.account_type || '',
            logoUrl: payload.logoUrl || payload.logo_url || '',
            isActive: payload.isActive !== false && payload.is_active !== false,
        };
        // Drop empty optional strings / null locationId — backends often 400
        // on `locationId: null` or blank required-looking fields.
        // Keep locationName always when present (required upsert key).
        const body = {};
        Object.keys(raw).forEach((key) => {
            const val = raw[key];
            if (key === 'locationId') {
                if (val != null && val !== '') body.locationId = val;
                return;
            }
            if (key === 'isActive') {
                body.isActive = !!val;
                return;
            }
            if (val == null) return;
            if (typeof val === 'string' && val.trim() === '') return;
            body[key] = typeof val === 'string' ? val.trim() : val;
        });
        // Backend validation requires `location` (not only locationName).
        // Mirror the name onto common aliases; omit null locationId above.
        if (body.locationName) {
            body.location = body.locationName;
            body.location_name = body.locationName;
        } else if (payload.location || payload.location_name) {
            const loc = String(payload.location || payload.location_name).trim();
            if (loc) {
                body.location = loc;
                body.locationName = loc;
                body.location_name = loc;
            }
        }
        if (!body.location && !body.locationName && body.locationId == null) {
            return {
                status: 'ERROR',
                message: 'location, locationId, or locationName is required to save Sattva company details.',
                data: null,
            };
        }

        const bodyKey = sattvaSaveBodyKey(body);
        // Same payload already rejected recently — soft-return without a second PUT.
        if (
            _sattvaSaveLastFailedKey === bodyKey
            && _sattvaSaveLastFailedResult
            && (Date.now() - _sattvaSaveLastFailedAt) < 15000
        ) {
            return { ..._sattvaSaveLastFailedResult, _deduped: true };
        }
        // Concurrent identical save (Save + Next) — share one in-flight promise.
        if (_sattvaSaveInFlight && _sattvaSaveInFlightKey === bodyKey) {
            return _sattvaSaveInFlight;
        }

        const run = (async () => {
            try {
                const res = await axios.put(
                    API_URL + 'invoice/config/sattva-company-details',
                    body,
                    { headers: AuthService.authHeader() },
                );
                if (res.data && (res.data.status === 'OK' || res.status === 200 || res.status === 201)) {
                    const row = res.data.data != null ? res.data.data : res.data;
                    const normalized = row && typeof row === 'object' && !Array.isArray(row)
                        ? { ...row, ...normalizeSattvaCompanyDetails(row) }
                        : row;
                    _sattvaSaveLastFailedKey = null;
                    _sattvaSaveLastFailedResult = null;
                    return {
                        status: 'OK',
                        message: (res.data && res.data.message) || 'Sattva company details saved.',
                        data: normalized,
                        _source: 'sattva-company-details',
                    };
                }
                return res.data || { status: 'ERROR', message: 'Unexpected save response.', data: null };
            } catch (err) {
                const code = err && err.response && err.response.status;
                // Some stacks expose POST before PUT — retry once on 405.
                if (code === 405) {
                    try {
                        const res = await axios.post(
                            API_URL + 'invoice/config/sattva-company-details',
                            body,
                            { headers: AuthService.authHeader() },
                        );
                        if (res.data && (res.data.status === 'OK' || res.status === 200 || res.status === 201)) {
                            const row = res.data.data != null ? res.data.data : res.data;
                            const normalized = row && typeof row === 'object' && !Array.isArray(row)
                                ? { ...row, ...normalizeSattvaCompanyDetails(row) }
                                : row;
                            _sattvaSaveLastFailedKey = null;
                            _sattvaSaveLastFailedResult = null;
                            return {
                                status: 'OK',
                                message: (res.data && res.data.message) || 'Sattva company details saved.',
                                data: normalized,
                                _source: 'sattva-company-details',
                            };
                        }
                        return res.data || { status: 'ERROR', message: 'Unexpected save response.', data: null };
                    } catch (postErr) {
                        const postCode = postErr && postErr.response && postErr.response.status;
                        if (postCode === 404 || postCode === 501 || !postCode) {
                            return {
                                status: 'SERVICE_UNAVAILABLE',
                                message: 'Backend must implement PUT/POST /api/invoice/config/sattva-company-details to upsert invoice_sattva_company_details. Invoice will continue with this invoice only.',
                                data: null,
                            };
                        }
                        return this._sattvaSaveErrorResult(postErr);
                    }
                }
                if (code === 404 || code === 501 || !code) {
                    return {
                        status: 'SERVICE_UNAVAILABLE',
                        message: 'Backend must implement PUT/POST /api/invoice/config/sattva-company-details to upsert invoice_sattva_company_details. Invoice will continue with this invoice only.',
                        data: null,
                    };
                }
                // 400/422: soft-fail so Create Invoice can continue; surface backend message once.
                if (code === 400 || code === 422) {
                    const result = this._sattvaSaveErrorResult(err, {
                        soft: true,
                        defaultMessage: 'Sattva company details were rejected by the server — invoice will continue with the details you entered.',
                    });
                    _sattvaSaveLastFailedKey = bodyKey;
                    _sattvaSaveLastFailedAt = Date.now();
                    _sattvaSaveLastFailedResult = result;
                    // Soft-fail is expected until backend accepts the contract —
                    // log once per deduped body (see _sattvaSaveLastFailedKey).
                    if (!result._logged) {
                        console.debug(
                            '[invoice-config] sattva-company-details save soft-fail',
                            code,
                            result.message,
                            {
                                location: body.location,
                                locationId: body.locationId,
                                locationName: body.locationName,
                            },
                        );
                        result._logged = true;
                    }
                    return result;
                }
                return this._sattvaSaveErrorResult(err);
            } finally {
                if (_sattvaSaveInFlightKey === bodyKey) {
                    _sattvaSaveInFlight = null;
                    _sattvaSaveInFlightKey = null;
                }
            }
        })();

        _sattvaSaveInFlightKey = bodyKey;
        _sattvaSaveInFlight = run;
        return run;
    }

    /** Normalize axios/API error bodies for sattva company upsert soft-fail paths. */
    _sattvaSaveErrorResult(err, { soft = false, defaultMessage } = {}) {
        const data = (err && err.response && err.response.data) || null;
        const fieldErrors = data && (data.errors || data.validation || data.details);
        let msg = (data && (data.message || data.error || data.msg))
            || (err && err.message)
            || defaultMessage
            || 'Failed to save Sattva company details.';
        if (fieldErrors && typeof fieldErrors === 'object' && !Array.isArray(fieldErrors)) {
            const parts = Object.keys(fieldErrors).map((k) => `${k}: ${fieldErrors[k]}`);
            if (parts.length) msg = `${msg} (${parts.join('; ')})`;
        } else if (Array.isArray(fieldErrors) && fieldErrors.length) {
            msg = `${msg} (${fieldErrors.join('; ')})`;
        }
        const status = soft ? 'SERVICE_UNAVAILABLE' : ((data && data.status) || 'ERROR');
        return data && typeof data === 'object'
            ? { ...data, status, message: msg }
            : { status, message: msg, data: null };
    }

    /**
     * Working-path lookup used by getSattvaCompanyDetails / getSattvaCompanyInfo.
     * Never sends ?locationId= to sattva-company-info (that query 404s here).
     *
     * Order:
     *   1. GET invoice/client-master-data?locationId=  — April 2026 path that
     *      auto-filled Create Invoice when a location was selected
     *   2. GET invoice/config → sattvaCompanyInfo (object / array / map)
     *      and optional bankDetails
     *   3. GET invoice/config/sattva-company-info with NO query, then pick
     *      the office client-side (?locationId= 404s here)
     */
    async _sattvaCompanyDetailsFromExistingConfig(locationId) {
        const normalizeRow = (row) => {
            if (!row || typeof row !== 'object') return null;
            const n = normalizeSattvaCompanyDetails(row);
            const merged = { ...row, ...n };
            return sattvaRowHasUsableFields(merged) ? merged : null;
        };

        const applyBank = (row, bankSrc) => {
            if (!row || !bankSrc || typeof bankSrc !== 'object') return row;
            const bank = normalizeSattvaCompanyDetails(bankSrc) || {};
            if (!row.bankName && bank.bankName) row.bankName = bank.bankName;
            if (!row.accountName && bank.accountName) row.accountName = bank.accountName;
            if (!row.accountNumber && bank.accountNumber) row.accountNumber = bank.accountNumber;
            if (!row.ifscCode && bank.ifscCode) row.ifscCode = bank.ifscCode;
            if (!row.accountType && bank.accountType) row.accountType = bank.accountType;
            return row;
        };

        let normalized = null;
        let source = 'invoice-config-fallback';
        let bankOnConfigFallback = null;

        const mergeFromInfo = (info, nextSource) => {
            if (!locationId && Array.isArray(info) && !normalized) {
                const rows = info.map(normalizeRow).filter(Boolean);
                if (rows.length) {
                    return {
                        status: 'OK',
                        message: 'Sattva company details loaded from ' + nextSource + '.',
                        data: rows,
                        _source: nextSource,
                    };
                }
                return null;
            }
            const picked = pickSattvaCompanyInfoRow(info, locationId);
            const fromInfo = picked ? normalizeRow(picked) : null;
            if (!fromInfo) return null;
            if (normalized) {
                Object.keys(fromInfo).forEach((key) => {
                    if (normalized[key] == null || normalized[key] === '') {
                        normalized[key] = fromInfo[key];
                    }
                });
            } else {
                normalized = fromInfo;
                source = nextSource;
            }
            return null;
        };

        // 1. Historical working location lookup (numeric id only — never a city name).
        if (locationId && !this._clientMasterByLocationMissing) {
            try {
                const master = await this._getSharedMasterData(locationId);
                if (master && master.status === 'OK' && master.data != null) {
                    const raw = Array.isArray(master.data)
                        ? (pickSattvaCompanyInfoRow(master.data, locationId) || master.data[0])
                        : master.data;
                    const fromMaster = normalizeRow(raw);
                    if (fromMaster) {
                        normalized = fromMaster;
                        source = 'client-master-data';
                    }
                }
            } catch (err) {
                const code = err && err.response && err.response.status;
                if (code === 404 || code === 501) this._clientMasterByLocationMissing = true;
            }
        }

        // 2. Aggregate GET invoice/config — fill gaps or stand in when master is empty.
        try {
            const cfg = await this._getSharedInvoiceConfig();
            if (cfg && cfg.status === 'OK' && cfg.data) {
                const info = pickField(cfg.data, ['sattvaCompanyInfo', 'sattva_company_info']);
                const bankOnConfig = pickField(cfg.data, ['bankDetails', 'bank_details']);
                const listResult = mergeFromInfo(info, 'invoice-config-fallback');
                if (listResult) return listResult;
                if (normalized) {
                    applyBank(normalized, bankOnConfig);
                    if (!normalized.bankName && !this._bankDetailsMissing) {
                        try {
                            const bankRes = await this.getBankDetails();
                            if (bankRes && bankRes.status === 'OK' && bankRes.data) {
                                applyBank(normalized, bankRes.data);
                            }
                        } catch (e) { /* ignore bank enrich */ }
                    }
                    return {
                        status: 'OK',
                        message: source === 'client-master-data'
                            ? 'Sattva company details loaded from client-master-data.'
                            : 'Sattva company details loaded from invoice/config fallback.',
                        data: normalized,
                        _source: source,
                    };
                }
                bankOnConfigFallback = bankOnConfig;
            }
        } catch (e) { /* fall through */ }

        // 3. Dedicated company-info list (no locationId — that query 404s here).
        if (!normalized || !sattvaRowHasUsableFields(normalized)) {
            try {
                const info = await this._getSharedSattvaCompanyInfoPayload();
                const listResult = mergeFromInfo(info, 'sattva-company-info');
                if (listResult) return listResult;
            } catch (e) { /* fall through */ }
        }

        if (normalized) {
            return {
                status: 'OK',
                message: source === 'client-master-data'
                    ? 'Sattva company details loaded from client-master-data.'
                    : (source === 'sattva-company-info'
                        ? 'Sattva company details loaded from sattva-company-info.'
                        : 'Sattva company details loaded from invoice/config fallback.'),
                data: normalized,
                _source: source,
            };
        }

        if (bankOnConfigFallback && sattvaRowHasUsableFields(bankOnConfigFallback)) {
            const bankOnly = normalizeRow(bankOnConfigFallback);
            if (bankOnly) {
                return {
                    status: 'OK',
                    message: 'Sattva company details loaded from invoice/config bankDetails fallback.',
                    data: bankOnly,
                    _source: 'invoice-config-fallback',
                };
            }
        }

        return this._sattvaCompanyDetailsUnavailable();
    }

    /**
     * GET invoice/config/sattva-company-info with no query string.
     * `?locationId=` 404s on this backend; the unscoped route is registered
     * (403 without a token). Cached; remembered as missing after 404.
     */
    async _getSharedSattvaCompanyInfoPayload() {
        if (this._sattvaCompanyInfoMissing) return null;
        const cached = this._sattvaCompanyInfoCache;
        if (cached) {
            if (cached.promise) return cached.promise;
            if (Date.now() - cached.timestamp < INVOICE_CONFIG_CACHE_TTL_MS) return cached.value;
        }
        const promise = axios.get(API_URL + 'invoice/config/sattva-company-info', {
            headers: AuthService.authHeader(),
        }).then((res) => {
            const data = res.data && (res.data.status === 'OK' || res.data.data != null)
                ? (res.data.data != null ? res.data.data : res.data)
                : null;
            this._sattvaCompanyInfoCache = { value: data, timestamp: Date.now() };
            return data;
        }).catch((err) => {
            const code = err && err.response && err.response.status;
            if (code === 404 || code === 501) this._sattvaCompanyInfoMissing = true;
            this._sattvaCompanyInfoCache = null;
            return null;
        });
        this._sattvaCompanyInfoCache = { promise };
        return promise;
    }

    /**
     * Populates the "Sattva Location" DROPDOWN itself — the list of issuing
     * offices a user picks FROM — from the same proposed dedicated table as
     * getSattvaCompanyDetails() above, called in its list-all mode (no
     * locationId). Per product direction, the issuing-office list and its
     * GST/bank/company details should ultimately come from ONE table
     * instead of the dropdown being sourced from the generic `/locations`
     * master list (shared across jobs/candidates/expenses/reimbursements —
     * see location-service.js) while the *details* for whichever one gets
     * picked come from a completely different lookup.
     *
     * IMPORTANT: this ONLY returns a non-null result when the new dedicated
     * endpoint is actually live and returned a real array
     * (`_source === 'sattva-company-details'`). If it 404s/501s (not
     * shipped yet, which is the current state) or the fallback kicks in,
     * this returns `null` — signaling callers to keep using the existing,
     * real, working `/locations` list (`locationService.getAll()`, used
     * identically across the whole app) rather than risk building a
     * wrongly-shaped dropdown out of `getMasterTableData()`'s fallback
     * response, which returns ONE generic client-master-data-shaped row,
     * not a proper list of issuing offices.
     */
    async getSattvaLocationOptions() {
        try {
            const res = await this.getSattvaCompanyDetails();
            if (
                res && res.status === 'OK' && res._source === 'sattva-company-details'
                && Array.isArray(res.data) && res.data.length
            ) {
                const options = res.data
                    .filter((d) => d && d.isActive !== false)
                    .map((d) => {
                        const rawId = d.locationId ?? d.location_id ?? d.id ?? null;
                        let id = null;
                        if (rawId != null && rawId !== '') {
                            const asNum = Number(rawId);
                            if (Number.isFinite(asNum) && String(asNum) === String(rawId).trim()) {
                                id = asNum;
                            }
                        }
                        return {
                            title: d.locationName || d.companyName || (id != null ? `Location ${id}` : ''),
                            id,
                        };
                    })
                    .filter((o) => o.title);
                if (options.length) {
                    return { status: 'OK', data: options, _source: 'sattva-company-details' };
                }
            }
        } catch (err) {
            // fall through — caller keeps using the existing /locations list
        }
        return null;
    }

    /**
     * Terminal "no data" path for getSattvaCompanyDetails() once the new
     * dedicated endpoint 404s/501s/errors — see that method's jsdoc for
     * the full story of what this replaced (a doomed fallback call to the
     * CLIENT master-data endpoint) and why it was wrong. Deliberately does
     * NOT make any network request — there is currently no correct
     * endpoint anywhere in this API to look Sattva's own company/bank
     * details up from, so firing one would either 400 (wrong param) or
     * return wrong/unrelated data (wrong table). Callers should treat this
     * exactly like the SERVICE_UNAVAILABLE pattern used elsewhere in this
     * file — clear the relevant fields and let the user fill them in
     * manually, rather than silently showing wrong info or a blank
     * unexplained state.
     */
    async _sattvaCompanyDetailsUnavailable() {
        return {
            status: 'SERVICE_UNAVAILABLE',
            message: 'Sattva company/bank details for this location are not available yet — the dedicated backend table/API for this has not been implemented. Please fill in manually.',
            data: null,
        };
    }

    /**
     * Aggregate invoice config. CONFIRMED (2026-07-16) via a real
     * authenticated `GET https://ats.sattvahuman.in/api/invoice/config`
     * (no query params) — returns `200 OK` with:
     *   { status: 'OK', message: '...', data: {
     *       sattvaCompanyInfo, paymentTerms, creditPeriods, gstRates,
     *       tdsPercentages, cancellationReasons, bookingReasons,
     *       bookingCheckDays, sacCodes, paymentModels, validationRules,
     *       einvoiceSchedule, documentTypes, notificationEmailTemplates,
     *       notificationSchedules, rateCard
     *   } }
     * This supersedes the earlier unverified guess that these fields lived
     * on the client-master-data response (getMasterTableData() above) — that
     * guess is no longer used by the getters below.
     *
     * As of the same confirmation, several sub-fields are genuinely empty on
     * the backend right now (not a frontend/routing bug): `paymentTerms`,
     * `gstRates`, `tdsPercentages`, `cancellationReasons`, `bookingReasons`,
     * `sacCodes`, `paymentModels` are `[]`, and `sattvaCompanyInfo`/
     * `validationRules` are `{}`, `rateCard` is `null`. Only `creditPeriods`
     * (`[15,30,45,60,90]`), `documentTypes`, `einvoiceSchedule`,
     * `bookingCheckDays`, `notificationEmailTemplates`/`notificationSchedules`
     * are populated. Callers already degrade gracefully to an empty
     * dropdown/list when a getter below returns `[]` — this is expected
     * until the backend populates that data, not something fixable here.
     */
    async getInvoiceConfig() {
        try {
            const res = await axios.get(API_URL + 'invoice/config', {
                headers: AuthService.authHeader()
            });
            if (res.data && res.data.status === 'OK' && res.data.data) {
                applyInvoiceColumnTypeCatalog(res.data.data);
            } else if (res.data && typeof res.data === 'object') {
                applyInvoiceColumnTypeCatalog(res.data);
            }
            return res.data;
        } catch (err) {
            return err.response;
        }
    }

    /**
     * Shared, short-lived wrapper around getInvoiceConfig(), mirroring
     * _getSharedMasterData() above: concurrent callers (the several config
     * getters below, typically fired together in a Promise.all on page
     * load) share one in-flight request, then reuse the resolved response
     * for INVOICE_CONFIG_CACHE_TTL_MS.
     */
    async _getSharedInvoiceConfig() {
        const cached = this._invoiceConfigCache;
        if (cached) {
            if (cached.promise) return cached.promise;
            if (Date.now() - cached.timestamp < INVOICE_CONFIG_CACHE_TTL_MS) return cached.value;
        }
        const promise = this.getInvoiceConfig()
            .then((result) => {
                this._invoiceConfigCache = { value: result, timestamp: Date.now() };
                return result;
            })
            .catch((err) => {
                this._invoiceConfigCache = null;
                throw err;
            });
        this._invoiceConfigCache = { promise };
        return promise;
    }

    /**
     * Same working lookup as getSattvaCompanyDetails. Never GETs
     * `invoice/config/sattva-company-info?locationId=` — that query 404s.
     * The unscoped `GET …/sattva-company-info` may still be used as a list.
     */
    async getSattvaCompanyInfo(locationId) {
        try {
            const res = await this._sattvaCompanyDetailsFromExistingConfig(locationId);
            if (res && res.status === 'OK' && res.data != null) {
                return { status: 'OK', data: res.data, _source: res._source };
            }
            return unavailable('Sattva company info', null);
        } catch (err) {
            return configUnavailable(err, 'Sattva company info');
        }
    }

    async getBankDetails() {
        try {
            const cfg = await this._getSharedInvoiceConfig();
            if (cfg && cfg.status === 'OK' && cfg.data) {
                const bank = pickField(cfg.data, ['bankDetails', 'bank_details']);
                if (bank && typeof bank === 'object') {
                    return { status: 'OK', data: bank };
                }
            }
        } catch (e) { /* try dedicated route once */ }

        if (this._bankDetailsMissing) {
            return unavailable('Bank details', null);
        }
        try {
            const res = await axios.get(API_URL + 'invoice/config/bank-details', {
                headers: AuthService.authHeader()
            });
            return res.data;
        } catch (err) {
            const code = err && err.response && err.response.status;
            if (code === 404 || code === 501 || !code) {
                if (code === 404 || code === 501) this._bankDetailsMissing = true;
                return unavailable('Bank details', null);
            }
            return err.response;
        }
    }

    /**
     * NOTE ON RESPONSE SHAPE (getSACCodes/getCreditPeriods/getPaymentTerms/
     * getTDSPercentages/getGSTRates/getPaymentModels/getCancellationReasons/
     * getBookingReasons/getDocumentTypes below):
     *
     * These used to either call dedicated `/invoice/config/*` sub-routes
     * that 404 on the backend, or (an earlier guess) read off
     * getMasterTableData()'s client-master-data response — both wrong. A
     * real authenticated `GET invoice/config` (no params) call CONFIRMED
     * the actual aggregate shape (see getInvoiceConfig()'s jsdoc above for
     * the full payload) — all nine of these getters now read from that one
     * cached call via _getSharedInvoiceConfig(), each still returning the
     * same `{ status: 'OK', data }` / SERVICE_UNAVAILABLE contract callers
     * already expect, so no call-site changes were needed.
     *
     * Several of these fields are genuinely `[]` on the backend right now
     * (see getInvoiceConfig() jsdoc) — that's real data-completeness, not a
     * bug here. `unavailable(...)` is only returned when the field is
     * missing/wrong-typed on the response, not merely empty, EXCEPT for
     * getGSTRates() — see its own comment below for why that one's
     * different.
     */
    async getSACCodes() {
        try {
            const cfg = await this._getSharedInvoiceConfig();
            if (cfg && cfg.status === 'OK' && cfg.data) {
                const codes = pickField(cfg.data, ['sacCodes', 'sac_codes']);
                if (Array.isArray(codes)) return { status: 'OK', data: codes };
            }
            return unavailable('SAC codes');
        } catch (err) {
            return configUnavailable(err, 'SAC codes');
        }
    }

    /**
     * RE-VERIFIED (2026-07-17, same live confirmed shape as getInvoiceConfig()'s
     * jsdoc): the aggregate `/invoice/config` response's dedicated
     * `paymentTerms` key is still a genuinely empty array on the backend —
     * checked the FULL payload again (not just the top-level key) for
     * anywhere payment-terms-like data might be hiding under a different
     * name (`notificationSchedules` is keyed by the SAME day-numbers as
     * `creditPeriods` — 15/30/45/60/90 — but its sub-objects are follow-up
     * EMAIL REMINDER schedules, not selectable "Net X days" terms;
     * `validationRules`/`sattvaCompanyInfo` are `{}`; `rateCard` is `null`).
     * So there is no hidden real `paymentTerms` data elsewhere in that
     * payload — it is simply not populated yet.
     *
     * However, `creditPeriods` (e.g. `[15, 30, 45, 60, 90]`) IS real,
     * populated, live data in the SAME response, and already drives the
     * separate "Credit Period" field in WizardPaymentStep.vue. Since a
     * "payment term" is conceptually just "Net N days" (or "Due on
     * Receipt"), we derive a usable Payment Terms list from that real data
     * instead of just showing a disabled/empty control — tagged
     * `_source: 'derived-from-credit-periods'` so callers can log/flag that
     * this isn't a dedicated backend payment-terms config, in case the
     * backend later ships one with different (e.g. named/non-numeric)
     * terms that should take priority.
     */
    async getPaymentTerms() {
        try {
            const cfg = await this._getSharedInvoiceConfig();
            if (cfg && cfg.status === 'OK' && cfg.data) {
                const terms = pickField(cfg.data, ['paymentTerms', 'payment_terms', 'paymentTermsOptions']);
                if (Array.isArray(terms) && terms.length) {
                    return { status: 'OK', data: terms, _source: 'backend' };
                }
                const periods = pickField(cfg.data, ['creditPeriods', 'credit_periods', 'creditPeriodOptions']);
                if (Array.isArray(periods) && periods.length) {
                    const derived = [
                        { name: 'Due on Receipt', days: 0 },
                        ...periods
                            .map((p) => parseInt(p, 10))
                            .filter((p) => !Number.isNaN(p) && p > 0)
                            .sort((a, b) => a - b)
                            .map((p) => ({ name: `Net ${p} days`, days: p })),
                    ];
                    return { status: 'OK', data: derived, _source: 'derived-from-credit-periods' };
                }
            }
            return unavailable('Payment terms');
        } catch (err) {
            return configUnavailable(err, 'Payment terms');
        }
    }

    // Business rules
    async getCreditPeriods() {
        try {
            const cfg = await this._getSharedInvoiceConfig();
            if (cfg && cfg.status === 'OK' && cfg.data) {
                const periods = pickField(cfg.data, ['creditPeriods', 'credit_periods', 'creditPeriodOptions']);
                if (Array.isArray(periods)) return { status: 'OK', data: periods };
            }
            return unavailable('Credit periods');
        } catch (err) {
            return configUnavailable(err, 'Credit periods');
        }
    }

    async getTDSPercentages() {
        try {
            const cfg = await this._getSharedInvoiceConfig();
            if (cfg && cfg.status === 'OK' && cfg.data) {
                const tds = pickField(cfg.data, ['tdsPercentages', 'tds_percentages', 'tdsRates']);
                if (Array.isArray(tds)) return { status: 'OK', data: tds };
            }
            return unavailable('TDS percentages');
        } catch (err) {
            return configUnavailable(err, 'TDS percentages');
        }
    }

    /**
     * GST rates. Confirmed live shape is an ARRAY (`"gstRates": []`), not
     * the `{ sgst, cgst, igst }` object this codebase's callers assume
     * (CreateInvoice.vue keeps a hardcoded `{ sgst: 9, cgst: 9, igst: 18 }`
     * default and only overwrites it when this getter reports 'OK' — see
     * its `loadConfigurations()`). Reporting 'OK' with an empty (or
     * unexpectedly-shaped) array here would blank out that object and break
     * GST math (`this.gstRates.sgst` etc. would become undefined → NaN).
     * So unlike the other getters in this group, an empty result here is
     * treated as unavailable on purpose, to protect callers' safe
     * hardcoded fallback until the backend actually populates real rate
     * entries and this + its callers get updated together for the real
     * populated shape.
     */
    async getGSTRates() {
        try {
            const cfg = await this._getSharedInvoiceConfig();
            if (cfg && cfg.status === 'OK' && cfg.data) {
                const rates = pickField(cfg.data, ['gstRates', 'gst_rates']);
                const hasUsableData = Array.isArray(rates)
                    ? rates.length > 0
                    : !!(rates && typeof rates === 'object' && Object.keys(rates).length > 0);
                if (hasUsableData) return { status: 'OK', data: rates };
            }
            return unavailable('GST rates');
        } catch (err) {
            return configUnavailable(err, 'GST rates');
        }
    }

    async getPaymentModels() {
        try {
            const cfg = await this._getSharedInvoiceConfig();
            if (cfg && cfg.status === 'OK' && cfg.data) {
                const models = pickField(cfg.data, ['paymentModels', 'payment_models']);
                if (Array.isArray(models)) return { status: 'OK', data: models };
            }
            return unavailable('Payment models');
        } catch (err) {
            return configUnavailable(err, 'Payment models');
        }
    }

    async getCancellationReasons() {
        try {
            const cfg = await this._getSharedInvoiceConfig();
            if (cfg && cfg.status === 'OK' && cfg.data) {
                const reasons = pickField(cfg.data, [
                    'cancellationReasons',
                    'cancellation_reasons',
                    'cancellationReasonOptions',
                    'cancellation_reason_options',
                ]);
                if (Array.isArray(reasons) && reasons.length) {
                    return { status: 'OK', data: reasons, _source: 'backend' };
                }
            }
            return { status: 'OK', data: DEFAULT_CANCELLATION_REASONS, _source: 'frontend-default' };
        } catch (err) {
            const fail = configUnavailable(err, 'Cancellation reasons');
            if (fail && fail.status === 'SERVICE_UNAVAILABLE') {
                return { status: 'OK', data: DEFAULT_CANCELLATION_REASONS, _source: 'frontend-default' };
            }
            return fail;
        }
    }

    async getDocumentTypes() {
        try {
            const cfg = await this._getSharedInvoiceConfig();
            if (cfg && cfg.status === 'OK' && cfg.data) {
                const types = pickField(cfg.data, ['documentTypes', 'document_types']);
                if (Array.isArray(types)) return { status: 'OK', data: types };
            }
            return unavailable('Document types');
        } catch (err) {
            return configUnavailable(err, 'Document types');
        }
    }

    async getBookingReasons() {
        try {
            const cfg = await this._getSharedInvoiceConfig();
            if (cfg && cfg.status === 'OK' && cfg.data) {
                const reasons = pickField(cfg.data, [
                    'bookingReasons',
                    'booking_reasons',
                    'bookingReasonOptions',
                    'booking_reason_options',
                ]);
                if (Array.isArray(reasons) && reasons.length) {
                    return { status: 'OK', data: reasons, _source: 'backend' };
                }
            }
            // GET /invoice/config currently ships bookingReasons: [] — same
            // empty-array problem as paymentTerms. Keep the dropdown usable
            // until the backend populates real values.
            return { status: 'OK', data: DEFAULT_BOOKING_REASONS, _source: 'frontend-default' };
        } catch (err) {
            const fail = configUnavailable(err, 'Booking reasons');
            if (fail && fail.status === 'SERVICE_UNAVAILABLE') {
                return { status: 'OK', data: DEFAULT_BOOKING_REASONS, _source: 'frontend-default' };
            }
            return fail;
        }
    }

    async getBookingCheckDays() {
        try {
            const res = await axios.get(API_URL + 'invoice/config/booking-check-days', {
                headers: AuthService.authHeader()
            });
            return res.data;
        } catch (err) {
            return err.response;
        }
    }

    // Notification schedules
    async getNotificationSchedules() {
        try {
            const res = await axios.get(API_URL + 'invoice/config/notification-schedules', {
                headers: AuthService.authHeader()
            });
            return res.data;
        } catch (err) {
            return err.response;
        }
    }

    async getNotificationSchedule(creditPeriod) {
        try {
            const res = await axios.get(API_URL + 'invoice/config/notification-schedule/' + creditPeriod, {
                headers: AuthService.authHeader()
            });
            return res.data;
        } catch (err) {
            return err.response;
        }
    }

    async getNotificationEmailTemplates() {
        try {
            const res = await axios.get(API_URL + 'invoice/config/notification-email-templates', {
                headers: AuthService.authHeader()
            });
            return res.data;
        } catch (err) {
            return err.response;
        }
    }

    // E-Invoice schedule
    async getEInvoiceSchedule() {
        try {
            const res = await axios.get(API_URL + 'invoice/config/einvoice-schedule', {
                headers: AuthService.authHeader()
            });
            return res.data;
        } catch (err) {
            return err.response;
        }
    }

    async getEInvoiceFilingDates() {
        try {
            const res = await axios.get(API_URL + 'invoice/config/einvoice-filing-dates', {
                headers: AuthService.authHeader()
            });
            return res.data;
        } catch (err) {
            return err.response;
        }
    }

    async getEInvoiceReminderTimes() {
        try {
            const res = await axios.get(API_URL + 'invoice/config/einvoice-reminder-times', {
                headers: AuthService.authHeader()
            });
            return res.data;
        } catch (err) {
            return err.response;
        }
    }

    // Rate card
    async getRateCard(clientId, vertical, category) {
        try {
            const params = {};
            if (clientId) params.clientId = clientId;
            if (vertical) params.vertical = vertical;
            if (category) params.category = category;
            const res = await axios.get(API_URL + 'invoice/config/rate-card', {
                params,
                headers: AuthService.authHeader()
            });
            return res.data;
        } catch (err) {
            return err.response;
        }
    }

    // Validation rules
    async getValidationRules() {
        try {
            const res = await axios.get(API_URL + 'invoice/config/validation-rules', {
                headers: AuthService.authHeader()
            });
            return res.data;
        } catch (err) {
            return err.response;
        }
    }

    async getDateValidationRules() {
        try {
            const res = await axios.get(API_URL + 'invoice/config/date-validation-rules', {
                headers: AuthService.authHeader()
            });
            return res.data;
        } catch (err) {
            return err.response;
        }
    }

    // Export templates
    async getExportTemplates() {
        try {
            const res = await axios.get(API_URL + 'invoice/config/export-templates', {
                headers: AuthService.authHeader()
            });
            return res.data;
        } catch (err) {
            return err.response;
        }
    }

    async getExportFilters() {
        try {
            const res = await axios.get(API_URL + 'invoice/config/export-filters', {
                headers: AuthService.authHeader()
            });
            return res.data;
        } catch (err) {
            return err.response;
        }
    }

    // ─── Invoice templates (NEW APIs — /invoice/config/templates) ───────────

    /** Wizard: best active template for client + category */
    async getTemplateForClient(clientId, category = null) {
        const params = { clientId };
        if (category) params.category = category;
        return this._withTemplateFallback(
            () => axios.get(this._templatePath(), { params, headers: AuthService.authHeader() }),
            () => {
                const match = findBestForClient(clientId, category);
                return { status: 'OK', data: match, _source: 'session' };
            },
            null,
        );
    }

    /** Admin: list templates (server-primary; session memory if API missing) */
    async listTemplates(filters = {}) {
        const result = await this._withTemplateFallback(
            () => axios.get(this._templatePath(), { params: filters, headers: AuthService.authHeader() }),
            () => ({ status: 'OK', data: listLocal(filters), _source: 'session' }),
            { status: 'ERROR', data: [], message: 'Failed to load templates.' },
        );
        if (result && result.status === 'OK') {
            observeTemplateColumnTypes(result.data);
        }
        return result;
    }

    async getTemplateById(id) {
        const result = await this._withTemplateFallback(
            () => axios.get(this._templatePath('/' + id), { headers: AuthService.authHeader() }),
            () => {
                const row = getLocalById(id);
                return row ? { status: 'OK', data: row, _source: 'session' } : null;
            },
            null,
        );
        if (result && result.status === 'OK' && result.data) {
            observeTemplateColumnTypes(result.data);
        }
        return result;
    }

    async createTemplate(payload, options = {}) {
        const status = options.activate ? 'active' : (payload.status || 'draft');
        const body = { ...payload, status };
        return this._withTemplateFallback(
            () => axios.post(this._templatePath(), body, { headers: AuthService.authHeader() }),
            () => ({ status: 'OK', data: createLocal(body), _source: 'session' }),
            { status: 'ERROR', message: 'Failed to create template. Backend template API is required.' },
            { mutating: true },
        );
    }

    async updateTemplate(id, payload) {
        return this._withTemplateFallback(
            () => axios.put(this._templatePath('/' + id), payload, { headers: AuthService.authHeader() }),
            () => {
                const updated = updateLocal(id, payload);
                return updated
                    ? { status: 'OK', data: updated, _source: 'session' }
                    : { status: 'ERROR', message: 'Template not found.' };
            },
            { status: 'ERROR', message: 'Failed to update template. Backend template API is required.' },
            { mutating: true },
        );
    }

    async cloneTemplate(sourceId, overrides = {}) {
        return this._withTemplateFallback(
            () => axios.post(this._templatePath('/' + sourceId + '/clone'), overrides, { headers: AuthService.authHeader() }),
            () => {
                const cloned = cloneLocal(sourceId, overrides);
                return cloned
                    ? { status: 'OK', data: cloned, _source: 'session' }
                    : { status: 'ERROR', message: 'Source template not found.' };
            },
            { status: 'ERROR', message: 'Failed to clone template. Backend template API is required.' },
            { mutating: true },
        );
    }

    async updateTemplateStatus(id, status) {
        return this._withTemplateFallback(
            () => axios.patch(this._templatePath('/' + id + '/status'), { status }, { headers: AuthService.authHeader() }),
            () => {
                const updated = updateStatusLocal(id, status);
                return updated
                    ? { status: 'OK', data: updated, _source: 'session' }
                    : { status: 'ERROR', message: 'Template not found.' };
            },
            { status: 'ERROR', message: 'Failed to update status. Backend template API is required.' },
            { mutating: true },
        );
    }

    async getTemplateVersions(id) {
        return this._withTemplateFallback(
            () => axios.get(this._templatePath('/' + id + '/versions'), { headers: AuthService.authHeader() }),
            () => ({ status: 'OK', data: getVersionsLocal(id), _source: 'session' }),
            { status: 'ERROR', data: [] },
        );
    }

    async deleteTemplate(id) {
        return this._withTemplateFallback(
            () => axios.delete(this._templatePath('/' + id), { headers: AuthService.authHeader() }),
            () => (removeLocal(id)
                ? { status: 'OK', message: 'Template deleted.', _source: 'session' }
                : { status: 'ERROR', message: 'Only draft templates can be deleted.' }),
            { status: 'ERROR', message: 'Failed to delete template. Backend template API is required.' },
            { mutating: true },
        );
    }

    _clientPreferencePath() {
        return API_URL + 'invoice/config/client-template-preference';
    }

    /** Browser fallback when PUT/GET client-template-preference is unavailable. */
    _clientPreferenceLocalKey(clientId) {
        return `invoice.clientTemplatePref.${String(clientId || '').trim()}`;
    }

    _readLocalClientTemplatePreference(clientId) {
        if (!clientId || typeof localStorage === 'undefined') return null;
        try {
            const raw = localStorage.getItem(this._clientPreferenceLocalKey(clientId));
            if (!raw) return null;
            const parsed = JSON.parse(raw);
            return parsed && typeof parsed === 'object' ? parsed : null;
        } catch (_) {
            return null;
        }
    }

    _writeLocalClientTemplatePreference(clientId, preference = {}) {
        if (!clientId || typeof localStorage === 'undefined') return;
        try {
            const payload = {
                clientId: String(clientId).trim(),
                line_item_preset_id: preference.lineItemPresetId
                    ?? preference.line_item_preset_id
                    ?? preference.templateId
                    ?? null,
                line_item_column_config: preference.lineItemColumnConfig
                    ?? preference.line_item_column_config
                    ?? null,
                line_item_column_config_by_preset: preference.lineItemColumnConfigByPreset
                    ?? preference.line_item_column_config_by_preset
                    ?? null,
                layout_style: preference.layoutStyle ?? preference.layout_style ?? null,
                category: preference.category ?? null,
                template_id: preference.dbTemplateId ?? preference.template_id ?? null,
                updatedAt: new Date().toISOString(),
            };
            localStorage.setItem(this._clientPreferenceLocalKey(clientId), JSON.stringify(payload));
        } catch (_) {
            /* private mode / quota */
        }
    }

    _clearLocalClientTemplatePreference(clientId) {
        if (!clientId || typeof localStorage === 'undefined') return;
        try {
            localStorage.removeItem(this._clientPreferenceLocalKey(clientId));
        } catch (_) {
            /* ignore */
        }
    }

    /**
     * Per-client Style + Line-items preference (backend source of truth).
     * Falls back to localStorage so Edit Columns survive "new invoice for
     * same client" when the preference API is missing.
     * Contract: backend-handoff/INVOICE_TEMPLATES_API_DOCUMENTATION.md §4.1 (server-side).
     */
    async getClientTemplatePreference(clientId) {
        if (!clientId) return { status: 'OK', data: null };
        const local = this._readLocalClientTemplatePreference(clientId);
        try {
            const res = await axios.get(this._clientPreferencePath(), {
                params: { clientId: String(clientId).trim() },
                headers: AuthService.authHeader(),
            });
            if (isApiSuccess(res.data) && res.data.data) return res.data;
            if (isApiSuccess(res.data) && !res.data.data && local) {
                return { status: 'OK', data: local, _source: 'local' };
            }
            if (res.data && res.data.status === 'NOT_IMPLEMENTED') {
                return { status: 'OK', data: local, _source: local ? 'local' : 'not_implemented' };
            }
            if (local) return { status: 'OK', data: local, _source: 'local' };
            return res.data || { status: 'ERROR', data: null };
        } catch (err) {
            if (shouldUseLocalFallback(err, err.response && err.response.data)) {
                return { status: 'OK', data: local, _source: local ? 'local' : 'not_implemented' };
            }
            if (local) return { status: 'OK', data: local, _source: 'local' };
            return (err.response && err.response.data) || { status: 'ERROR', data: null };
        }
    }

    /**
     * Upsert per-client invoice create preferences (preset, columns, layout).
     * Always mirrors to localStorage; soft-fails when the route is not implemented yet.
     */
    async saveClientTemplatePreference(clientId, preference = {}) {
        if (!clientId) return { status: 'ERROR', message: 'clientId is required.' };
        const body = {
            clientId: String(clientId).trim(),
            client_id: String(clientId).trim(),
            line_item_preset_id: preference.lineItemPresetId
                ?? preference.line_item_preset_id
                ?? preference.templateId
                ?? null,
            line_item_column_config: preference.lineItemColumnConfig
                ?? preference.line_item_column_config
                ?? null,
            line_item_column_config_by_preset: preference.lineItemColumnConfigByPreset
                ?? preference.line_item_column_config_by_preset
                ?? null,
            layout_style: preference.layoutStyle ?? preference.layout_style ?? null,
            category: preference.category ?? null,
            template_id: preference.dbTemplateId ?? preference.template_id ?? null,
        };
        this._writeLocalClientTemplatePreference(clientId, preference);
        try {
            const res = await axios.put(this._clientPreferencePath(), body, {
                headers: AuthService.authHeader(),
            });
            if (isApiSuccess(res.data)) return res.data;
            return res.data || { status: 'ERROR', message: 'Failed to save client template preference.' };
        } catch (err) {
            if (shouldUseLocalFallback(err, err.response && err.response.data)) {
                // Local mirror already written — treat as soft success so create flow
                // still restores columns on the next invoice for this client.
                return {
                    status: 'OK',
                    data: body,
                    message: 'Saved line-item column preference locally (preference API not available).',
                    _source: 'local',
                };
            }
            return (err.response && err.response.data) || { status: 'ERROR', message: 'Failed to save client template preference.' };
        }
    }

    /** Clear stored preference when user picks Auto (recommended). */
    async clearClientTemplatePreference(clientId) {
        if (!clientId) return { status: 'OK' };
        this._clearLocalClientTemplatePreference(clientId);
        try {
            const res = await axios.delete(this._clientPreferencePath(), {
                params: { clientId: String(clientId).trim() },
                headers: AuthService.authHeader(),
            });
            if (isApiSuccess(res.data)) return res.data;
            return res.data || { status: 'OK' };
        } catch (err) {
            if (shouldUseLocalFallback(err, err.response && err.response.data)) {
                return { status: 'OK', _source: 'local' };
            }
            return (err.response && err.response.data) || { status: 'ERROR' };
        }
    }

    /**
     * Persist Style + Line-items choice for a client (wizard toolbar / create success).
     * Uses the client-template-preference endpoint; POST /invoice also sends the same
     * fields for invoice-row audit — backend SHOULD write-back to client preference.
     */
    async persistClientInvoicePreferences(clientId, preference = {}) {
        return this.saveClientTemplatePreference(clientId, preference);
    }

    /** @deprecated Use listTemplates() */
    async getInvoiceTemplates() {
        return this.listTemplates();
    }

    /**
     * Client Master Data.
     *
     * CONFIRMED (2026-07-16): calls `invoice/config/client-master-data`, WITH
     * the `config/` prefix. This was previously changed to drop the `config/`
     * prefix based on an unverified assumption that it 404s like the other
     * dead `config/*` routes — that assumption was wrong. Unauthenticated
     * probes of the live backend show BOTH `invoice/client-master-data` and
     * `invoice/config/client-master-data` return 403 "No token provided!"
     * (i.e. both are registered routes — a genuinely missing route returns a
     * clean 404 here, confirmed by probing a bogus path), so 403-vs-404
     * alone can't disambiguate which one actually serves real data. The
     * user directly confirmed (with a real, successful `status: "OK"`
     * response body for `clientName=HPE`) that the `config/`-prefixed URL —
     * `https://ats.sattvahuman.in/api/invoice/config/client-master-data?clientName=HPE`
     * — is the one that actually returns client-keyed master data
     * (gstin_no, company_address, state_name, supply_location, po_number,
     * credit_period, vertical, tds_rate, einvoice_address, legal_name — the
     * fields CreateInvoice.vue/InvoiceCreateManual.vue destructure from this
     * call). Reverting to that path here.
     *
     * NOTE: getMasterTableData() above (Sattva's own issuing-office info, by
     * `locationId`) still calls the NON-`config/`-prefixed
     * `invoice/client-master-data`. That path was NOT verified against real
     * data in this pass — only `getClientMasterData()` (this method) had a
     * confirmed-working response pasted by the user. Left as-is; revisit if
     * Sattva company info also turns out to be misrouted.
     */
    /**
     * Client master data for invoice auto-fill.
     *
     * @param {string} clientName — required
     * @param {string|null} stateName — optional; when provided the backend
     *   SHOULD return only rows for that state (proposed query param
     *   `stateName`). Until the backend supports it, callers filter the
     *   array response client-side via findClientEntityByState().
     */
    async getClientMasterData(clientName, stateName = null) {
        try {
            const params = { clientName };
            if (stateName) params.stateName = stateName;
            const res = await axios.get(API_URL + 'invoice/config/client-master-data', {
                params,
                headers: AuthService.authHeader()
            });
            return res.data;
        } catch (err) {
            return err.response;
        }
    }

    /**
     * Create a new client (+ first entity row) from the invoice wizard.
     *
     * PROPOSED CONTRACT (backend team — not implemented yet):
     *   POST /invoice/config/clients
     *   Body: {
     *     client_name: string (required),
     *     legal_name?: string,
     *     gstin_no?: string,
     *     company_address?: string,
     *     state_name?: string (required for multi-state clients),
     *     supply_location?: string,
     *     phone?: string,
     *     vertical?: string,
     *     credit_period?: number,
     *     tds_rate?: number
     *   }
     *   Response 201: { status: 'OK', data: { id, client_name, ...entity fields } }
     *
     * FALLBACK: if the route 404s/501s, posts to the existing `jobs/client`
     * endpoint (name-only create) so the dropdown still gets a new entry.
     * Full GST/address fields are returned as `_pendingBackend: true` so
     * callers can warn that master-data sync needs the real endpoint.
     */
    async createInvoiceClient(payload) {
        const body = {
            client_name: payload.client_name || payload.clientName,
            legal_name: payload.legal_name || payload.legalName || '',
            gstin_no: payload.gstin_no || payload.gstin || payload.clientGST || '',
            company_address: payload.company_address || payload.address || payload.clientAddress || '',
            state_name: payload.state_name || payload.state || payload.clientState || '',
            supply_location: payload.supply_location || payload.placeOfSupply || '',
            phone: payload.phone || payload.clientPhone || '',
            vertical: payload.vertical || '',
            credit_period: payload.credit_period ?? null,
            tds_rate: payload.tds_rate ?? null,
        };
        try {
            const res = await axios.post(
                API_URL + 'invoice/config/clients',
                body,
                { headers: AuthService.authHeader() },
            );
            return res.data;
        } catch (err) {
            const code = err && err.response && err.response.status;
            if (code === 404 || code === 501 || !code) {
                return this._createInvoiceClientFallback(body);
            }
            return (err.response && err.response.data) || {
                status: 'ERROR',
                message: (err && err.message) || 'Failed to create client',
            };
        }
    }

    async _createInvoiceClientFallback(body) {
        try {
            const res = await axios.post(
                API_URL + 'jobs/client',
                { client_name: body.client_name },
                { headers: AuthService.authHeader() },
            );
            const data = (res && res.data) || {};
            return {
                status: data.status || 'OK',
                message: 'Client name saved via jobs/client (full master-data fields pending backend POST /invoice/config/clients).',
                data: {
                    client_name: body.client_name,
                    ...body,
                    ...(data.data || data),
                },
                _source: 'jobs-client-fallback',
                _pendingBackend: true,
            };
        } catch (err) {
            return {
                status: 'ERROR',
                message: (err && err.message) || 'Could not create client',
            };
        }
    }

    /**
     * Get line item template type for a client/category.
     * Returns 'rpo' for RPO-style invoices (e.g. KPMG)
     * Returns 'generic' for standard invoices
     */
    getClientInvoiceLineItemTemplate(clientName, category) {
        if (!clientName || !category) return 'generic'
        const client = String(clientName).toLowerCase()
        const cat    = String(category).toLowerCase()
        const isRecruitmentOrContractual = cat.includes('recruitment') || cat.includes('contractual')
        const rpoClientPatterns = ['kpmg', 'rpo']
        const isRpoClient = rpoClientPatterns.some(p => client.includes(p))
        if (isRecruitmentOrContractual && isRpoClient) return 'rpo'
        return 'generic'
    }
}

export default new InvoiceConfigService();
